import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { AlertDialogCancel } from "@patient-watch/ui/alert-dialog";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { zodResolver } from "@hookform/resolvers/zod";
import { DEFAULT_LOCALE, LOCALE_LABELS, type SupportedLocale } from "@patient-watch/i18n";
import { ConsentLocaleTabs, localesFromSavedTranslations } from "@/features/consent/components/ConsentLocaleTabs";
import { ConsentFieldLabelRow } from "@/features/consent/components/ConsentFieldLabelRow";
import { SaveConsentBodyDialog } from "@/features/consent/components/SaveConsentBodyDialog";
import { SaveConsentTranslationDialog } from "@/features/consent/components/SaveConsentTranslationDialog";
import {
    applyConsentTranslateProgress,
    INITIAL_CONSENT_TRANSLATE_PROGRESS,
    type ConsentTranslateProgress,
} from "@/features/consent/ai/consentAiProgress";
import { translateConsentCopy } from "@/features/consent/ai/runConsentAi";
import type { ConsentCopyPayload } from "@/features/consent/ai/consentCopySchema";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RichTextEditor } from "@/features/consent/components/RichTextEditor";
import { useConsentBodyById, useConsentBodyTranslations } from "@/features/consent/api/selectConsentBodies";
import { useDeleteConsentBody } from "@/features/consent/api/deleteConsentBody";
import { useDeleteConsentBodyTranslation } from "@/features/consent/api/deleteConsentBodyTranslation";
import {
    useUpdateConsentBodyVersion,
    useUpsertConsentBody,
    useUpsertConsentBodyTranslation,
} from "@/features/consent/api/upsertConsentBody";
import { incrementConsentVersion } from "@/features/consent/utils/incrementConsentVersion";
import { patientFacingCopyChanged } from "@/features/consent/utils/patientFacingCopyChanged";
import { toast } from "@patient-watch/ui/sonner";

const consentBodyFormSchema = z.object({
    consent_name: z.string().min(1, "Name is required"),
    summary_text: z.string().optional(),
    attestation_text: z.string().optional(),
    document_url: z.string().optional(),
    is_published: z.boolean(),
    is_optional: z.boolean(),
    grants_care_team_sharing: z.boolean(),
    grants_analytics_sharing: z.boolean(),
});

type ConsentBodyFormT = z.infer<typeof consentBodyFormSchema>;

const DEFAULT_VALUES: ConsentBodyFormT = {
    consent_name: "",
    summary_text: "",
    attestation_text: "",
    document_url: "",
    is_published: false,
    is_optional: false,
    grants_care_team_sharing: false,
    grants_analytics_sharing: false,
};

function toConsentPayload(copy: {
    consent_name: string;
    summary_text: string;
    attestation_text: string;
}): ConsentCopyPayload {
    return {
        consent_name: copy.consent_name,
        summary_text: copy.summary_text,
        attestation_text: copy.attestation_text,
        lang: DEFAULT_LOCALE,
    };
}

const EditConsentBody = () => {
    const { consentBodyId } = useParams({ from: "/_app/builder/consents/$consentBodyId" });
    const navigate = useNavigate();
    const isNew = consentBodyId === "new";
    const [editorLocale, setEditorLocale] = useState<SupportedLocale>(DEFAULT_LOCALE);
    const [openLocales, setOpenLocales] = useState<SupportedLocale[]>([DEFAULT_LOCALE]);
    const [isAddingTranslation, setIsAddingTranslation] = useState(false);
    const [translateProgress, setTranslateProgress] = useState<ConsentTranslateProgress | null>(null);
    const [translatingLocale, setTranslatingLocale] = useState<SupportedLocale | null>(null);
    const [pendingAiDrafts, setPendingAiDrafts] = useState<Partial<Record<SupportedLocale, ConsentCopyPayload>>>({});
    const [saveTranslationOpen, setSaveTranslationOpen] = useState(false);
    const [deleteTranslationOpen, setDeleteTranslationOpen] = useState(false);
    const [saveConsentOpen, setSaveConsentOpen] = useState(false);
    const [pendingVersionBump, setPendingVersionBump] = useState<{ from: string; to: string } | null>(null);
    const [deleteConsentOpen, setDeleteConsentOpen] = useState(false);
    const skipFormResetRef = useRef(false);
    const editorLocaleRef = useRef(editorLocale);
    editorLocaleRef.current = editorLocale;
    const isCanonicalLocale = editorLocale === DEFAULT_LOCALE;

    const { data: consentBody, isLoading } = useConsentBodyById(consentBodyId, { enabled: !isNew });
    const { data: translations = [] } = useConsentBodyTranslations(consentBodyId, { enabled: !isNew });
    const { mutateAsync: upsertConsentBody, isPending: isSavingBody } = useUpsertConsentBody();
    const { mutateAsync: upsertConsentBodyTranslation, isPending: isSavingTranslation } =
        useUpsertConsentBodyTranslation();
    const { mutateAsync: updateConsentBodyVersionMutation, isPending: isUpdatingVersion } =
        useUpdateConsentBodyVersion();
    const { mutateAsync: deleteConsentBodyTranslation, isPending: isDeletingTranslation } =
        useDeleteConsentBodyTranslation();
    const { mutateAsync: deleteConsentBody, isPending: isDeletingConsent } = useDeleteConsentBody();
    const isPending =
        isSavingBody || isSavingTranslation || isUpdatingVersion || isDeletingTranslation || isDeletingConsent;

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Consents", route: { to: "/builder/consents" } },
        { label: isNew ? "New" : "Edit", route: { to: "/builder/consents" } },
    ]);

    const form = useForm<ConsentBodyFormT>({
        resolver: zodResolver(consentBodyFormSchema),
        defaultValues: DEFAULT_VALUES,
    });

    useEffect(() => {
        if (isNew) return;
        setOpenLocales((prev) => {
            const merged = localesFromSavedTranslations([...prev, ...translations.map((row) => row.locale)]);
            return merged;
        });
    }, [isNew, translations]);

    useEffect(() => {
        if (!consentBody) return;
        if (skipFormResetRef.current) {
            skipFormResetRef.current = false;
            return;
        }

        if (isCanonicalLocale) {
            form.reset({
                consent_name: consentBody.consent_name ?? "",
                summary_text: consentBody.summary_text ?? "",
                attestation_text: consentBody.attestation_text ?? "",
                document_url: consentBody.document_url ?? "",
                is_published: consentBody.is_published ?? false,
                is_optional: consentBody.is_optional ?? false,
                grants_care_team_sharing: consentBody.grants_care_team_sharing ?? false,
                grants_analytics_sharing: consentBody.grants_analytics_sharing ?? false,
            });
            return;
        }

        const translation = translations.find((row) => row.locale === editorLocale);
        const draft = pendingAiDrafts[editorLocale];
        form.reset({
            consent_name: draft?.consent_name ?? translation?.consent_name ?? "",
            summary_text: draft?.summary_text ?? translation?.summary_text ?? "",
            attestation_text: draft?.attestation_text ?? translation?.attestation_text ?? "",
            document_url: translation?.document_url ?? "",
            is_published: consentBody.is_published ?? false,
            is_optional: consentBody.is_optional ?? false,
            grants_care_team_sharing: consentBody.grants_care_team_sharing ?? false,
            grants_analytics_sharing: consentBody.grants_analytics_sharing ?? false,
        });
    }, [consentBody, editorLocale, form, isCanonicalLocale, pendingAiDrafts, translations]);

    const watchedName = form.watch("consent_name");
    const watchedSummary = form.watch("summary_text");
    const watchedAttestation = form.watch("attestation_text");

    const canonicalCopy = useMemo(() => {
        if (consentBody && !isNew) {
            return {
                consent_name: consentBody.consent_name ?? "",
                summary_text: consentBody.summary_text ?? "",
                attestation_text: consentBody.attestation_text ?? "",
            };
        }
        return {
            consent_name: watchedName ?? "",
            summary_text: watchedSummary ?? "",
            attestation_text: watchedAttestation ?? "",
        };
    }, [consentBody, isNew, watchedAttestation, watchedName, watchedSummary]);

    const handleAddTranslation = async (locale: SupportedLocale, useAiTranslation: boolean) => {
        if (isNew || !consentBody) return;

        setOpenLocales((prev) => localesFromSavedTranslations([...prev, locale]));
        setEditorLocale(locale);

        if (!useAiTranslation) {
            return;
        }

        setTranslatingLocale(locale);
        setTranslateProgress(INITIAL_CONSENT_TRANSLATE_PROGRESS);
        setIsAddingTranslation(true);
        try {
            const [translation] = await translateConsentCopy(toConsentPayload(canonicalCopy), [locale], (update) => {
                setTranslateProgress((previous) =>
                    applyConsentTranslateProgress(previous ?? INITIAL_CONSENT_TRANSLATE_PROGRESS, update)
                );
            });
            if (!translation) {
                throw new Error("AI translation returned no copy.");
            }
            if (editorLocaleRef.current === locale) {
                skipFormResetRef.current = true;
                form.setValue("consent_name", translation.consent_name, { shouldDirty: true });
                form.setValue("summary_text", translation.summary_text, { shouldDirty: true });
                form.setValue("attestation_text", translation.attestation_text, { shouldDirty: true });
            }
            setPendingAiDrafts((previous) => ({ ...previous, [locale]: translation }));
            toast.success(`${LOCALE_LABELS[locale]} translation drafted. Review and save when ready.`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "AI translation failed.");
        } finally {
            setIsAddingTranslation(false);
            setTranslateProgress(null);
            setTranslatingLocale(null);
        }
    };

    const handleApplyField = (field: "consent_name" | "summary_text" | "attestation_text", value: string) => {
        form.setValue(field, value, { shouldDirty: true });
    };

    const fieldContext = useMemo(
        () => ({
            consent_name: watchedName ?? "",
            summary_text: watchedSummary ?? "",
            attestation_text: watchedAttestation ?? "",
        }),
        [watchedAttestation, watchedName, watchedSummary]
    );

    const onSubmit = async (values: ConsentBodyFormT) => {
        if (!isCanonicalLocale) return;

        await upsertConsentBody({
            ...(isNew ? {} : { consent_body_id: consentBodyId }),
            consent_name: values.consent_name,
            consent_version: pendingVersionBump?.to ?? consentBody?.consent_version ?? "1.0",
            summary_text: values.summary_text || null,
            attestation_text: values.attestation_text || null,
            document_url: values.document_url || null,
            is_published: values.is_published,
            is_optional: values.is_optional,
            grants_care_team_sharing: values.grants_care_team_sharing,
            grants_analytics_sharing: values.grants_analytics_sharing,
        });

        if (pendingVersionBump && !isNew) {
            await updateConsentBodyVersionMutation({
                consentBodyId,
                consentVersion: pendingVersionBump.to,
            });
        }

        await navigate({ to: "/builder/consents" });
    };

    const handleOpenSaveConsent = async () => {
        const valid = await form.trigger();
        if (!valid) return;

        if (consentBody && patientFacingCopyChanged(form.getValues(), consentBody)) {
            const from = consentBody.consent_version ?? "1.0";
            setPendingVersionBump({ from, to: incrementConsentVersion(from) });
        } else {
            setPendingVersionBump(null);
        }

        setSaveConsentOpen(true);
    };

    const handleSaveConsent = async () => {
        await form.handleSubmit(onSubmit)();
    };

    const handleDeleteConsent = async () => {
        await deleteConsentBody(consentBodyId);
        setDeleteConsentOpen(false);
        await navigate({ to: "/builder/consents" });
    };

    const handleOpenSaveTranslation = async () => {
        const valid = await form.trigger(["consent_name", "summary_text", "attestation_text", "document_url"]);
        if (!valid) return;
        setSaveTranslationOpen(true);
    };

    const handleSaveTranslation = async ({
        updateVersion,
        consentVersion,
    }: {
        updateVersion: boolean;
        consentVersion: string;
    }) => {
        const values = form.getValues();
        await upsertConsentBodyTranslation({
            consent_body_id: consentBodyId,
            locale: editorLocale,
            consent_name: values.consent_name.trim() || null,
            summary_text: values.summary_text || null,
            attestation_text: values.attestation_text || null,
            document_url: values.document_url || null,
        });

        if (updateVersion) {
            await updateConsentBodyVersionMutation({ consentBodyId, consentVersion });
        }

        setPendingAiDrafts((previous) => {
            const next = { ...previous };
            delete next[editorLocale];
            return next;
        });
    };

    const handleDeleteTranslation = async () => {
        const hasSavedTranslation = translations.some((row) => row.locale === editorLocale);

        if (hasSavedTranslation) {
            await deleteConsentBodyTranslation({ consentBodyId, locale: editorLocale });
        }

        setOpenLocales((prev) => prev.filter((locale) => locale !== editorLocale));
        setPendingAiDrafts((previous) => {
            const next = { ...previous };
            delete next[editorLocale];
            return next;
        });
        setEditorLocale(DEFAULT_LOCALE);
        setDeleteTranslationOpen(false);
    };

    if (!isNew && isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 gap-6 mx-auto p-2 md:p-4 pb-16 max-w-xl min-h-full">
            <h1 className="font-semibold text-3xl tracking-tight">{isNew ? "New Consent" : "Edit Consent"}</h1>

            <div className="space-y-1 text-muted-foreground text-sm">
                <p>English (UK) is the canonical copy. Other languages overlay it when patients read the consent.</p>
                <p>
                    {isNew
                        ? "Save the English copy first, then add languages with the + button beside the tabs."
                        : "Add a whole language with the + button beside the tabs. Use the AI button beside a single field to redraft or check just that field."}
                </p>
            </div>

            <ConsentLocaleTabs
                value={editorLocale}
                onValueChange={setEditorLocale}
                openLocales={openLocales}
                onAddLocale={handleAddTranslation}
                addDisabled={isNew}
                addDisabledReason="Save the English copy first, then add languages."
                isAdding={isAddingTranslation}
                translateProgress={translateProgress}
                translatingLocale={translatingLocale}
            />

            <ui.Form {...form}>
                <form
                    onSubmit={
                        isNew
                            ? form.handleSubmit(onSubmit)
                            : (event) => {
                                  event.preventDefault();
                              }
                    }
                    className="flex flex-col flex-1 gap-6"
                >
                    <ConsentFieldLabelRow
                        field="consent_name"
                        editorLocale={editorLocale}
                        currentValue={watchedName ?? ""}
                        canonicalValue={canonicalCopy.consent_name}
                        context={fieldContext}
                        onApply={(value) => handleApplyField("consent_name", value)}
                    >
                        <fc.Input
                            name="consent_name"
                            placeholder={
                                isCanonicalLocale
                                    ? "e.g. Municipality reporting consent"
                                    : consentBody?.consent_name || "Translated name"
                            }
                        />
                    </ConsentFieldLabelRow>

                    <fc.Input name="document_url" label="Document URL (optional)" placeholder="https://..." />

                    <ConsentFieldLabelRow
                        field="summary_text"
                        editorLocale={editorLocale}
                        currentValue={watchedSummary ?? ""}
                        canonicalValue={canonicalCopy.summary_text}
                        context={fieldContext}
                        onApply={(value) => handleApplyField("summary_text", value)}
                        className="flex flex-col flex-1 gap-2 min-h-[calc(100dvh-22rem)]"
                    >
                        <RichTextEditor
                            className="flex flex-col flex-1 min-h-0"
                            value={form.watch("summary_text") ?? ""}
                            onChange={(html) => form.setValue("summary_text", html, { shouldDirty: true })}
                            placeholder={
                                isCanonicalLocale
                                    ? "Write the consent content the patient will read..."
                                    : "Translated consent body. Leave blank to keep English."
                            }
                        />
                    </ConsentFieldLabelRow>

                    <ConsentFieldLabelRow
                        field="attestation_text"
                        editorLocale={editorLocale}
                        currentValue={watchedAttestation ?? ""}
                        canonicalValue={canonicalCopy.attestation_text}
                        context={fieldContext}
                        onApply={(value) => handleApplyField("attestation_text", value)}
                    >
                        <fc.Textarea
                            name="attestation_text"
                            placeholder="I confirm that I have read, understood, and agree to the terms outlined in this consent form."
                        />
                    </ConsentFieldLabelRow>

                    {isCanonicalLocale ? (
                        <>
                            <fc.Switch name="is_published" label="Published (available to attach to templates)" />
                            <fc.Switch name="is_optional" label="Patient may decline this consent" />
                            <fc.Switch
                                name="grants_care_team_sharing"
                                label="Stop care-team sharing if the patient withdraws"
                                description="While this consent is signed, the care team can see the diary. Withdrawal turns that off."
                            />
                            <fc.Switch
                                name="grants_analytics_sharing"
                                label="Stop analytics sharing if the patient withdraws"
                                description="While this consent is signed, the diary can be included in analytics. Withdrawal excludes it."
                            />
                        </>
                    ) : null}

                    <div className="flex justify-end gap-2 pb-8">
                        {isNew ? (
                            <fc.SaveButton
                                label="Save Consent"
                                size="default"
                                className="w-auto"
                                disabled={isPending}
                            />
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        isCanonicalLocale ? setDeleteConsentOpen(true) : setDeleteTranslationOpen(true)
                                    }
                                    disabled={isPending}
                                >
                                    Delete
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() =>
                                        void (isCanonicalLocale ? handleOpenSaveConsent() : handleOpenSaveTranslation())
                                    }
                                    disabled={isPending}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </form>
            </ui.Form>

            {!isNew && isCanonicalLocale ? (
                <>
                    <SaveConsentBodyDialog
                        open={saveConsentOpen}
                        onOpenChange={setSaveConsentOpen}
                        consentName={watchedName ?? ""}
                        onConfirm={handleSaveConsent}
                        isPending={isSavingBody || isUpdatingVersion}
                        fromVersion={pendingVersionBump?.from}
                        toVersion={pendingVersionBump?.to}
                    />

                    <ui.AlertDialog open={deleteConsentOpen} onOpenChange={setDeleteConsentOpen}>
                        <ui.AlertDialogContent>
                            <ui.AlertDialogHeader>
                                <ui.AlertDialogTitle>Delete consent?</ui.AlertDialogTitle>
                                <ui.AlertDialogDescription>
                                    Are you sure you want to delete{" "}
                                    <strong>{consentBody?.consent_name ?? "this consent"}</strong>? This cannot be
                                    undone.
                                </ui.AlertDialogDescription>
                            </ui.AlertDialogHeader>
                            <ui.AlertDialogFooter className="flex sm:flex-row flex-col-reverse gap-2 sm:space-x-0">
                                <AlertDialogCancel asChild>
                                    <Button type="button" variant="outline" className="w-full">
                                        Cancel
                                    </Button>
                                </AlertDialogCancel>
                                <ui.AlertDialogAction asChild>
                                    <fc.DeleteButton
                                        size="default"
                                        className="w-full"
                                        disabled={isDeletingConsent}
                                        onClick={() => void handleDeleteConsent()}
                                    />
                                </ui.AlertDialogAction>
                            </ui.AlertDialogFooter>
                        </ui.AlertDialogContent>
                    </ui.AlertDialog>
                </>
            ) : null}

            {!isNew && !isCanonicalLocale ? (
                <>
                    <SaveConsentTranslationDialog
                        open={saveTranslationOpen}
                        onOpenChange={setSaveTranslationOpen}
                        locale={editorLocale}
                        currentVersion={consentBody?.consent_version ?? "1.0"}
                        onConfirm={handleSaveTranslation}
                        isPending={isSavingTranslation || isUpdatingVersion}
                    />

                    <ui.AlertDialog open={deleteTranslationOpen} onOpenChange={setDeleteTranslationOpen}>
                        <ui.AlertDialogContent>
                            <ui.AlertDialogHeader>
                                <ui.AlertDialogTitle>Delete translation?</ui.AlertDialogTitle>
                                <ui.AlertDialogDescription>
                                    Are you sure you want to delete the {LOCALE_LABELS[editorLocale]} translation? This
                                    cannot be undone.
                                </ui.AlertDialogDescription>
                            </ui.AlertDialogHeader>
                            <ui.AlertDialogFooter className="flex sm:flex-row flex-col-reverse gap-2 sm:space-x-0">
                                <AlertDialogCancel asChild>
                                    <Button type="button" variant="outline" className="w-full">
                                        Cancel
                                    </Button>
                                </AlertDialogCancel>
                                <ui.AlertDialogAction asChild>
                                    <fc.DeleteButton
                                        size="default"
                                        className="w-full"
                                        disabled={isDeletingTranslation}
                                        onClick={() => void handleDeleteTranslation()}
                                    />
                                </ui.AlertDialogAction>
                            </ui.AlertDialogFooter>
                        </ui.AlertDialogContent>
                    </ui.AlertDialog>
                </>
            ) : null}
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/consents/$consentBodyId")({
    component: EditConsentBody,
});
